import { getAuthItem } from '@/lib/authStorage'

/**
 * Append auth token as _t query param so <img>, <video>, <audio>, and <iframe>
 * tags can authenticate through the supervisor gateway.
 * These HTML tags cannot send Authorization headers, so the supervisor's
 * getAuthInfo() reads the JWT from the _t query parameter instead.
 *
 * Handles URLs with hash fragments correctly (inserts _t before the fragment).
 * Guards against double-appending if _t is already present.
 */
export function appendAuthToken(url: string): string {
  // An empty url must STAY empty (falsy). Returning '?_t=<token>' for ''
  // produced a truthy-but-broken relative URL that won `src={fileUrl || blobUrl}`
  // fallbacks — drive-cached images (no storageUrl) rendered as alt text because
  // the broken '?_t=…' beat the working blob object-URL (#3797).
  if (!url) return url
  const token = typeof window !== 'undefined' ? getAuthItem('xavior_auth_token') : null
  if (!token) return url

  // Don't append auth tokens to CDN/R2 URLs — they're public and tokens defeat caching
  const r2PublicUrl = import.meta.env.VITE_R2_PUBLIC_URL || 'https://web.xavior.ai';
  if (url.startsWith(r2PublicUrl)) {
    return url;
  }

  // Guard against double-append (match only the _t query param, not substrings like content_type=)
  if (url.includes('?_t=') || url.includes('&_t=')) return url

  // Split off hash fragment so _t goes into query string, not the fragment
  const hashIdx = url.indexOf('#')
  const base = hashIdx >= 0 ? url.slice(0, hashIdx) : url
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : ''

  const sep = base.includes('?') ? '&' : '?'
  return `${base}${sep}_t=${encodeURIComponent(token)}${hash}`
}
